import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { ReactComponent as EnterIconSVG } from '../../assets/images/enter-icon.svg';
import { nextTick } from "process";
import LoadingSpinner from "../LoadingSpinner";

const AgentInput = forwardRef<
  { setContent: (content: string) => void },
  { onSend: (content: string) => Promise<boolean>; disabled?: boolean }
>(({ onSend, disabled }, ref) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [content, setContent] = useState<string>();
  const [isSending, setIsSending] = useState<boolean>(false);
  const isBlocked = !!disabled || isSending;
  
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setContent(value);
  };

  useImperativeHandle(ref, () => ({
    setContent: (content: string) => {
      if (!isSending) {
        setContent(content);
        textareaRef.current?.focus();
      }
    }
  }));

  useEffect(
    () => {
      textareaRef.current?.focus();
    },
    []
  );

  useEffect(
    () => {
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        const scrollHeight = textareaRef.current.scrollHeight;
        textareaRef.current.style.height = scrollHeight + 'px';
      }
    },
    [content]
  );

  const send = async () => {
    if (isBlocked) return;
    if (content) {
      setIsSending(true);
      if (await onSend(content)) {
        setContent('');
      }
      setIsSending(false);
      nextTick(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      });
    } else if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  return (
    <div className="absolute w-full bottom-0 left-0 rounded-lg pb-0">
      {/* Flat, clean input (Lovable-like) using Back4App colors */}
      <div className="mx-0 p-4">
        <div className="w-full flex items-center gap-3 glass-input glass-input-opaque px-4 py-3 focus-within:ring-2 focus-within:ring-[#208AEC]/60">
          <textarea
            disabled={isBlocked}
            className="m-0 flex-1 resize-none bg-transparent outline-none text-white placeholder:text-light-grey/70 leading-6"
            style={{ maxHeight: '120px' }}
            ref={textareaRef}
            placeholder={'Write a message...'}
            value={content}
            rows={1}
            onChange={handleChange}
            onKeyDown={async (event) => {
              if (event.key.toLowerCase() === 'enter') {
                event.preventDefault();
                if (isBlocked) return;
                if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
                  if (textareaRef.current) {
                    const startPos = textareaRef.current.selectionStart;
                    const endPos = textareaRef.current.selectionEnd;
                    const value = textareaRef.current.value;
                    const newValue = value.substring(0, startPos) + '\n' + value.substring(endPos);
                    setContent(newValue);
                    nextTick(() => {
                      if (textareaRef.current) {
                        textareaRef.current.setSelectionRange(startPos + 1, startPos + 1);
                        if (textareaRef.current.value.length === startPos + 1) {
                          textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
                        }
                      }
                    });
                  }
                } else {
                  send();
                }
              }
            }}
          />
          {isBlocked ? (
            <LoadingSpinner width='2rem' height='2rem' />
          ) : (
            <button
              onClick={send}
              className="flex-none inline-flex items-center justify-center w-10 h-10 rounded-md bg-white/10 text-light-blue hover:bg-white/15 transition-colors"
              aria-label="Send message"
            >
              <EnterIconSVG width="20" height="20" />
            </button>
          )}
        </div>
      </div>
    </div>
  )
});

export default AgentInput;